import Modal, { ModalProps } from "@/Components/UI/Modal";
import axios from "axios";
import { useEffect } from "react";
import { RouteName } from "ziggy-js";

export type DataLoadingModalProps<ModalData> = ModalProps & {
    id: string;
    route: RouteName;
    modalData?: ModalData;
    finishedLoadingCallback?: (modalData: ModalData | undefined) => void;
    clearOnClose?: boolean; // If true, clears the modalData when the modal is closed so the data is fresh every time
};

/**
 * If the parent component already has the necessary data, it can be passed into the modalData prop and the modal
 * will not need to fetch the data. However, if the parent doesn't have the necessary data, the modal will fetch it
 * and return the data to the parent component with the finishedLoadingCallback.
 */
export default function DataLoadingModal<ModalData>(
    props: DataLoadingModalProps<ModalData>,
) {
    const {
        children,
        route,
        modalData,
        finishedLoadingCallback,
        clearOnClose = true,
    } = props;

    const getModalData = async () => {
        const { data } = await axios.get<ModalData | undefined>(route);
        finishedLoadingCallback?.(data);
    };

    useEffect(() => {
        if (props.show && !modalData) {
            getModalData();
        }
    }, [props.show]);

    return (
        <Modal
            {...props}
            onClose={() => {
                if (clearOnClose) {
                    finishedLoadingCallback?.(undefined);
                }
                props.onClose();
            }}
        >
            {modalData ? (
                children
            ) : (
                <div className="flex min-h-64 min-w-64 flex-col items-center justify-center">
                    <img
                        src={`${import.meta.env.VITE_CDN_URL}/images/loadingStrawberry.gif`}
                        alt="Loading..."
                        className="mb-4 w-12"
                    />
                    Loading...
                </div>
            )}
        </Modal>
    );
}
